/**
 * KinsCircle Intelligence Layer V1 — Feature Flag
 *
 * Single source of truth for the `ENABLE_KINSCIRCLE_INTELLIGENCE` flag and
 * the optional `ENABLE_AI_REASONING` flag. Both default to FALSE so the
 * intelligence layer is invisible and inert in production unless an
 * operator explicitly enables it.
 *
 * Resolution order (first match wins):
 *   1. window.__KINSCIRCLE_INTELLIGENCE__ === true            (runtime override)
 *   2. localStorage 'kinscircle.intelligence.enabled' === '1' (persistent override)
 *   3. import.meta.env.VITE_ENABLE_KINSCIRCLE_INTELLIGENCE     (build-time)
 *   4. false                                                   (default)
 *
 * Hard contract — strictly read-only:
 *   - This module never mutates anything.
 *   - It never auto-repairs, never auto-merges, never auto-deletes.
 *   - It never overwrites relationships.
 *   - It never modifies the spouse / in-law engine, tree rendering,
 *     billing, uploads, auth, schema, or homepage.
 *   - When the flag is FALSE, every intelligence component must render
 *     null and every helper must early-return without side effects.
 *
 * Pure additive defense per STABILITY.md §1-§8.
 */

const FLAG_LS_KEY = 'kinscircle.intelligence.enabled';
const AI_LS_KEY = 'kinscircle.intelligence.ai';

const readLocal = (key: string): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const readEnv = (key: string): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env ?? {};
    const val = env[key];
    if (typeof val === 'string') {
      return val === 'true' || val === '1';
    }
    if (typeof val === 'boolean') return val;
  } catch {
    /* non-fatal — running in a context without import.meta */
  }
  return false;
};

const readWindow = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(window as any)[key];
  } catch {
    return false;
  }
};

/**
 * Master flag. When false (default), every intelligence component must
 * render null and every helper must early-return. No user-facing UI may
 * appear, no behavior may change, no saves may be affected.
 */
export const isIntelligenceEnabled = (): boolean => {
  if (readWindow('__KINSCIRCLE_INTELLIGENCE__')) return true;
  if (readLocal(FLAG_LS_KEY)) return true;
  if (readEnv('VITE_ENABLE_KINSCIRCLE_INTELLIGENCE')) return true;
  return false;
};

/**
 * AI reasoning flag — gates any LLM call. The heuristic / rule-based
 * intelligence layer works WITHOUT this flag (no API cost, no crash).
 * AI is purely an optional augmentation when an operator has both
 * intelligence enabled AND has an OpenAI / Gateway key configured server
 * side. The frontend NEVER sees the key — calls would route through an
 * edge function only.
 */
export const isAIReasoningEnabled = (): boolean => {
  if (!isIntelligenceEnabled()) return false;
  if (readWindow('__KINSCIRCLE_AI__')) return true;
  if (readLocal(AI_LS_KEY)) return true;
  if (readEnv('VITE_ENABLE_AI_REASONING')) return true;
  return false;
};

/**
 * Command Center operational-authority gate. Even when the feature flag
 * is enabled, only active Command Center principals may run these tools. Normal users still see only the
 * read-only "Guided Onboarding Assistant" guidance — never raw
 * diagnostics.
 */
export const canRunIntelligenceDiagnostics = (
  commandCenterOperationalAuthority: boolean,
): boolean => {
  if (!isIntelligenceEnabled()) return false;
  return commandCenterOperationalAuthority;
};

/**
 * Convenience setter for QA / dev — flips the persistent override on
 * the current browser. Never called from product UI; exposed for the
 * console / tests. Toggling never mutates any backend state.
 */
export const setIntelligenceFlagLocal = (enabled: boolean): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (enabled) window.localStorage.setItem(FLAG_LS_KEY, '1');
    else window.localStorage.removeItem(FLAG_LS_KEY);
  } catch {
    /* non-fatal */
  }
};
